import { AsyncPipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  ViewChild,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import { VexPopoverService } from '@pharvio/core/ui';
import { isLoaded } from '@pharvio/core/shared';
import {
  LoadAvailabilitySettings,
  RefreshCurrentAvailability,
  StorefrontAvailabilityState
} from '@pharvio/storefront/data-access';
import { fromEvent, interval } from 'rxjs';
import { map } from 'rxjs/operators';
import { AvailabilityPopoverComponent } from '../availability-popover/availability-popover.component';

/** Poll the computed availability every 45 minutes while the tab is visible. */
const POLL_INTERVAL_MS = 45 * 60 * 1000;

/**
 * ERP shell header pill showing live storefront availability. Projected into the
 * shared toolbar (`vexToolbarStart`) and clickable to open the status popover.
 */
@Component({
  selector: 'app-availability-status-pill',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, NgClass],
  templateUrl: './availability-status-pill.component.html'
})
export class AvailabilityStatusPillComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly popover = inject(VexPopoverService);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('originRef', { static: true, read: ElementRef })
  originRef?: ElementRef;

  dropdownOpen = false;

  readonly isOnline$ = this.store.select(StorefrontAvailabilityState.isOnline);
  readonly currentAvailability$ = this.store.select(
    StorefrontAvailabilityState.currentAvailability
  );
  /** Only show the pill once availability has loaded (avoids a "Closed" flash). */
  readonly hasLoaded$ = this.currentAvailability$.pipe(map(isLoaded));

  ngOnInit(): void {
    this.store.dispatch(new LoadAvailabilitySettings());

    // Poll on a 15-minute cadence, only while the document is visible.
    interval(POLL_INTERVAL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (document.visibilityState === 'visible') {
          this.store.dispatch(new RefreshCurrentAvailability());
        }
      });

    // Refresh immediately when the user returns to the tab.
    fromEvent(document, 'visibilitychange')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (document.visibilityState === 'visible') {
          this.store.dispatch(new RefreshCurrentAvailability());
        }
      });
  }

  openPopover(): void {
    if (!this.originRef) return;
    this.dropdownOpen = true;

    const ref = this.popover.open({
      content: AvailabilityPopoverComponent,
      origin: this.originRef,
      offsetY: 12,
      width: '320px',
      position: [
        {
          originX: 'end',
          originY: 'bottom',
          overlayX: 'end',
          overlayY: 'top'
        },
        {
          originX: 'center',
          originY: 'top',
          overlayX: 'center',
          overlayY: 'bottom'
        }
      ]
    });

    ref.afterClosed$.subscribe(() => (this.dropdownOpen = false));
  }
}
